import { type InfraCostsSummary } from '@carsfinders/contracts';

import { t } from '../../../lib/i18n';
import { monthKeyLongLabel, monthKeyShortLabel } from '../../finance/labels';
import { formatUsd } from '../format';

type Month = InfraCostsSummary['months'][number];

/**
 * Segment colours, one per service in order of first appearance — the same
 * service keeps its colour in every bar and in the legend. Design D tokens
 * only; `cf-danger` is left out on purpose (red reads as "overdue" here).
 */
export const SEGMENT_CLASSES = [
  'bg-cf-brand',
  'bg-cf-accent',
  'bg-cf-info',
  'bg-cf-success',
  'bg-cf-warn',
  'bg-cf-ink-soft',
  'bg-cf-ink-faint',
] as const;

/** Bar area height in px (the cap label and the axis label sit outside it). */
const BAR_PX = 120;

/** Every service that appears in any month, in order of first appearance. */
export function chartServices(months: readonly Month[]): { serviceId: string; serviceName: string }[] {
  const seen = new Map<string, string>();
  for (const month of months) {
    for (const entry of month.byService) {
      if (!seen.has(entry.serviceId)) seen.set(entry.serviceId, entry.serviceName);
    }
  }
  return [...seen.entries()].map(([serviceId, serviceName]) => ({ serviceId, serviceName }));
}

function monthTitle(month: Month): string {
  return t('admin.infraCosts.chart.barTitle', {
    month: monthKeyLongLabel(month.month),
    total: formatUsd(month.totalUsd),
  });
}

/**
 * Twelve stacked bars, one per month of `summary.months` — modelled on
 * `finance/components/revenue-bar-chart.tsx` (plain DOM, inline heights).
 *
 * The picture is `aria-hidden`; each bar carries a `title` for the mouse, and
 * the `sr-only` table below holds the same numbers for assistive technology.
 */
export function InfraScheduleChart({ months }: { months: readonly Month[] }) {
  const services = chartServices(months);
  const colourOf = new Map(
    services.map((s, index) => [s.serviceId, SEGMENT_CLASSES[index % SEGMENT_CLASSES.length]!]),
  );
  const max = Math.max(0, ...months.map((m) => m.totalUsd));

  return (
    <div className="flex flex-col gap-3">
      <div
        aria-hidden="true"
        data-testid="infra-chart"
        className="flex h-[168px] items-end gap-1.5 border-t border-cf-line-soft pt-1.5 lg:gap-2.5"
      >
        {months.map((month) => (
          <div
            key={month.month}
            data-testid="infra-chart-bar"
            data-month={month.month}
            title={monthTitle(month)}
            className="flex h-full min-w-0 flex-1 flex-col items-stretch justify-end gap-px"
          >
            <span className="mb-0.5 hidden truncate text-center font-mono text-[10px] text-cf-ink-subtle sm:block">
              {month.totalUsd > 0 ? formatUsd(month.totalUsd) : ''}
            </span>
            {[...month.byService].reverse().map((entry) => (
              <div
                key={entry.serviceId}
                data-service={entry.serviceId}
                className={`${colourOf.get(entry.serviceId) ?? SEGMENT_CLASSES[0]} first:rounded-t-[3px]`}
                style={{ height: `${max > 0 ? Math.max(entry.usd > 0 ? 2 : 0, (entry.usd / max) * BAR_PX) : 0}px` }}
              />
            ))}
            <span className="mt-1 text-center font-mono text-[10px] text-cf-ink-subtle">
              {monthKeyShortLabel(month.month)}
            </span>
          </div>
        ))}
      </div>

      {services.length > 0 && (
        <ul aria-hidden="true" className="flex flex-wrap gap-x-4 gap-y-1.5 text-[12px] text-cf-ink-muted">
          {services.map((service) => (
            <li key={service.serviceId} className="inline-flex items-center gap-1.5">
              <span className={`h-2.5 w-2.5 rounded-cf-xs ${colourOf.get(service.serviceId)}`} />
              {service.serviceName}
            </li>
          ))}
        </ul>
      )}

      <table className="sr-only" data-testid="infra-chart-table">
        <caption>{t('admin.infraCosts.chart.tableCaption')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('admin.infraCosts.chart.month')}</th>
            <th scope="col">{t('admin.infraCosts.chart.total')}</th>
            <th scope="col">{t('admin.infraCosts.chart.breakdown')}</th>
          </tr>
        </thead>
        <tbody>
          {months.map((month) => (
            <tr key={month.month}>
              <th scope="row">{monthKeyLongLabel(month.month)}</th>
              <td>{formatUsd(month.totalUsd)}</td>
              <td>
                {month.byService.length > 0
                  ? month.byService.map((e) => `${e.serviceName}: ${formatUsd(e.usd)}`).join('; ')
                  : t('admin.infraCosts.chart.none')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
